import React, { Dispatch, SetStateAction } from "react";
import { Button, Modal, Image } from "react-bootstrap";
import { AdminDashboardUser, OrgInfo, DashboardUsage } from "@preveil-api";
import { DashboardChartTitles, DashboardUsage as DashboardUsageUtil, useAppDispatch, UsageSecuritySettings } from "src/common";
import { Icon } from "src/components";
import { PrintUserTable } from "./dashboard-users";
import { uiActions } from "src/store/ui-manager/slice";
import logo from "src/assets/images/logos/preveil-logo.svg";

type AllProps = {
  org_info: OrgInfo;
  show_modal: boolean;
  users_list: AdminDashboardUser[];
  licenses?: number;
  dashboard_usage?: DashboardUsage;
  security_settings?: UsageSecuritySettings;
  setShowModal: Dispatch<SetStateAction<boolean>>;
}

function PrintModalComponent(props: AllProps) {
  const { org_info, show_modal, setShowModal, users_list, licenses, dashboard_usage, security_settings } = props;
  const dispatch = useAppDispatch();

  function print() {
    // Clear the message from the ui manager to
    dispatch(uiActions.handleMessageDismiss());
    setTimeout(() => {
      window.print();
    }, 0);
  }

  return (
    <>
      <Modal
        show={show_modal}
        size="xl"
        fullscreen="xl-down"
        className="print-modal"
        scrollable={true}
      >
        <Modal.Header className="print-modal-header">
          <Modal.Title> {org_info.org_name} Usage Report</Modal.Title>
          <div className="print-modal-header-buttons">
            <Button variant="icon" onClick={print}>
              <Icon className="ficon-printer" />
            </Button>
            <Button variant="icon" size="sm" onClick={() => { setShowModal(false); }}>
              <Icon className="pv-icon-close" />
            </Button>
          </div>
          <Image alt="PreVeil Logo" src={logo} className="print-logo" />
        </Modal.Header>
        <Modal.Body>
          {!!licenses && (
            <div className="section">
              <div className="section-header">
                <h3>Team Usage</h3>
                <p>
                  {new Date().toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })}
                </p>
              </div>
              <p> {licenses} license{licenses > 1 ? "s" : ""} used</p>
            </div>
          )}
          {!!dashboard_usage && (
            <>
              <div className="section">
                <h3>Storage</h3>
                <p> {DashboardUsageUtil.convertUsageToFriendlySize(dashboard_usage?.used ?? 0)} {DashboardChartTitles.used}</p>
                <p> {DashboardUsageUtil.convertUsageToFriendlySize((dashboard_usage?.available ?? 0))} {DashboardChartTitles.available}</p>
              </div>
              <div className="section">
                <h3>{DashboardChartTitles.used}</h3>
                <p> {DashboardUsageUtil.convertUsageToFriendlySize(dashboard_usage?.shared ?? 0)} {DashboardChartTitles.shared}</p>
                <p> {DashboardUsageUtil.convertUsageToFriendlySize(dashboard_usage?.private ?? 0)} {DashboardChartTitles.private}</p>
              </div>
            </>
          )}
          {!!security_settings && (
            <div className="section">
              <h3>Security Status</h3>
              <div>
                <div className="security-setting-row">
                  <span>Controlled Unclassified Information (CUI)</span>
                  <span className="dot-filler"></span>
                  <span>{security_settings.present_cui_notice ? "On" : "Off"}</span>
                </div>
                <p className="fst-italic">The CUI banner is displayed on PreVeil applications.</p>
              </div>
              <div>
                <div className="security-setting-row">
                  <span>Mobile Phone File Export</span>
                  <span className="dot-filler"></span>
                  {/* NOTE: The "security_settings.disable_file_export" is inverted because the API returns "false" for enabled and "true" for disabled */}
                  <span>{!security_settings.disable_file_export ? "On" : "Off"}</span>
                </div>
                <p className="fst-italic">PreVeil files can be downloaded to mobile devices.</p>
              </div>
              <div>
                <div className="security-setting-row">
                  <span>Biometric Requirements</span>
                  <span className="dot-filler"></span>
                  <span>{security_settings.require_biometrics ? "On" : "Off"}</span>
                </div>
                <p className="fst-italic">Biometrics are required for login on the PreVeil mobile app.</p>
              </div>
            </div>
          )}
          {!!users_list && users_list.length > 0 && (
            <PrintUserTable users_list={users_list} />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}

export default React.memo(PrintModalComponent);
